/**
 * 指挥页轨迹查询 / 下载时间范围 / 历史 KML（从 Dashboard 拆出）
 */
import dayjs from 'dayjs';
import { classifyNetworkQuality } from './devicePerfHelpers';
import { PLAYBACK_STEP_MS } from './constants';
import { sanitizeTrackPoints } from './trackSegmentHelpers';
import { toDisplay } from './basemapCoord';
import { notifyMemberMarkersProjectionReady } from './updateMemberMarkersOnMap';
import { syncPlaybackCursorMarkers, pickPlaybackPointForTrack } from './playbackCursorMarkers';

/**
 * 根据轨迹模式换算 device/list 的 display_days
 */
export function resolveDisplayDaysFromTrackMode(trackMode, options = {}) {
  const {
    trackMinutes = 0,
    trackHours = 0,
    trackDays = 0,
    startTime = '',
    endTime = ''
  } = options;

  if (trackMode === 'recent_days') {
    if (trackDays === 0) return { displayDays: 3650, reason: '全部历史 (天数为0)' };
    return { displayDays: trackDays, reason: `最近${trackDays}天` };
  }
  if (trackMode === 'recent_hours') {
    if (trackHours === 0) return { displayDays: 3650, reason: '全部历史 (小时数为0)' };
    const displayDays = Math.ceil(trackHours / 24);
    return { displayDays, reason: `最近${trackHours}小时 (约${displayDays}天)` };
  }
  if (trackMode === 'recent') {
    if (trackMinutes === 0) return { displayDays: 3650, reason: '全部历史 (分钟数为0)' };
    const displayDays = Math.ceil(trackMinutes / (24 * 60));
    return { displayDays, reason: `最近${trackMinutes}分钟 (约${displayDays}天)` };
  }
  if (trackMode === 'range' && startTime && endTime) {
    return { displayDays: 3650, reason: '时间范围模式，显示最近10年数据' };
  }
  return { displayDays: null, reason: 'fallback_map_settings' };
}

/**
 * 构建 /api/history/track URL
 * @param {object} member
 * @param {{
 *   trackMode?: string,
 *   trackMinutes?: number,
 *   trackHours?: number,
 *   trackDays?: number,
 *   startTime?: string,
 *   endTime?: string,
 *   playbackRange?: { start?: any, end?: any }
 * }} options
 */
export function buildHistoryTrackUrl(member, options = {}) {
  const {
    trackMode,
    trackMinutes = 0,
    trackHours = 0,
    trackDays = 0,
    startTime = '',
    endTime = '',
    playbackRange,
    /** 弹窗显式选择的时间范围；优先于全局轨迹模式 */
    explicitRange = null
  } = options;

  const params = new URLSearchParams();
  // uid 稳定后 history 常仍挂在旧 id/展示名：两个都传，后端 OR 合并
  if (member?.device_unique_id) params.set('device_unique_id', member.device_unique_id);
  if (member?.name) params.set('name', member.name);

  let url = `/api/history/track?${params.toString()}`;

  if (explicitRange && typeof explicitRange === 'object') {
    if (explicitRange.start) url += `&start=${encodeURIComponent(explicitRange.start)}`;
    if (explicitRange.end) url += `&end=${encodeURIComponent(explicitRange.end)}`;
    return url;
  }

  if (trackMode === 'recent') {
    if (trackMinutes !== 0) url += `&mode=recent&minutes=${trackMinutes}`;
  } else if (trackMode === 'recent_hours') {
    if (trackHours !== 0) url += `&mode=recent&hours=${trackHours}`;
  } else if (trackMode === 'recent_days') {
    if (trackDays !== 0) url += `&mode=recent&days=${trackDays}`;
  } else if (trackMode === 'range' && startTime && endTime) {
    url += `&start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`;
  }

  if (playbackRange?.start && playbackRange?.end) {
    url += `&start=${encodeURIComponent(playbackRange.start)}&end=${encodeURIComponent(playbackRange.end)}`;
  }

  return url;
}

function normalizeHistoryTrackPoints(points = []) {
  return sanitizeTrackPoints(points)
    .map((p) => {
      const lng = parseFloat(p.longitude ?? p.lng);
      const lat = parseFloat(p.latitude ?? p.lat);
      const alt = p.altitude != null ? parseFloat(p.altitude) : 0;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      const timeRaw = p.timestamp || p.time || p.recorded_at || p.located_at || p.created_at || null;
      let timeIso = null;
      if (timeRaw != null) {
        const d = new Date(timeRaw);
        if (!Number.isNaN(d.getTime())) timeIso = d.toISOString();
      }
      return {
        lng,
        lat,
        alt: Number.isFinite(alt) ? alt : 0,
        timeIso
      };
    })
    .filter(Boolean);
}

/** 历史轨迹点生成谷歌地球兼容 KML（LineString 折线，非离散 Point） */
export function buildHistoryTrackKml(memberName, points = []) {
  const coords = normalizeHistoryTrackPoints(points);
  const name = escapeXmlText(memberName || 'track');

  let kml = `<?xml version="1.0" encoding="UTF-8"?>`;
  kml += `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>`;
  kml += `<name>${name}</name>`;
  if (coords.length > 0) {
    kml += `<Placemark><name>${name}</name>`;
    kml += `<Style><LineStyle><color>ff0000ff</color><width>3</width></LineStyle></Style>`;
    kml += `<LineString><tessellate>1</tessellate><altitudeMode>clampToGround</altitudeMode><coordinates>`;
    kml += coords.map((p) => `${p.lng},${p.lat},${p.alt}`).join(' ');
    kml += `</coordinates></LineString></Placemark>`;
  }
  kml += `</Document></kml>`;
  return kml;
}

/** 历史轨迹点生成 GPX 1.1（trk/trkseg，谷歌地球 / 多数 GIS 可导入） */
export function buildHistoryTrackGpx(memberName, points = []) {
  const coords = normalizeHistoryTrackPoints(points);
  const name = escapeXmlText(memberName || 'track');
  const trkpts = coords.map((p) => {
    const timeTag = p.timeIso ? `\n        <time>${p.timeIso}</time>` : '';
    const eleTag = Number.isFinite(p.alt) ? `\n        <ele>${p.alt}</ele>` : '';
    return `      <trkpt lat="${p.lat}" lon="${p.lng}">${eleTag}${timeTag}\n      </trkpt>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="ZHXT Rescue" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${name}</name>
    <time>${new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${name}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
}

function escapeXmlText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function triggerTextDownload(filename, content, mime = 'application/vnd.google-earth.kml+xml') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function sanitizeDownloadFilenamePart(value) {
  return String(value || 'track').replace(/[\\/:*?"<>|\s~]/g, '_');
}

/**
 * 按时间范围拉取历史轨迹并导出（默认 KML+GPX，兼容谷歌地球）
 * @returns {Promise<{ ok: boolean, points?: number, label?: string, error?: string, insufficient?: boolean, formats?: string[] }>}
 */
export async function downloadMemberTrackAsKml(options = {}) {
  const {
    member,
    timeRange,
    fetchFn,
    dayjsLib = dayjs,
    // 'kml' | 'gpx' | 'both'
    format = 'both'
  } = options;

  if (!member || !timeRange) {
    return { ok: false, error: '缺少队员或时间范围' };
  }

  const params = new URLSearchParams();
  if (member.device_unique_id) params.set('device_unique_id', member.device_unique_id);
  if (member.name) params.set('name', member.name);
  if (timeRange.start) params.set('start', timeRange.start);
  if (timeRange.end) params.set('end', timeRange.end);

  const url = `/api/history/track?${params.toString()}`;
  console.log('下载轨迹请求URL:', url);
  const res = await fetchFn(url);
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error || '查询轨迹失败');
  }

  const points = Array.isArray(data.track) ? data.track : [];
  if (points.length < 2) {
    return { ok: false, insufficient: true, label: timeRange.label, points: points.length };
  }

  const safeName = sanitizeDownloadFilenamePart(member.name || member.device_unique_id || 'track');
  const safeLabel = sanitizeDownloadFilenamePart(timeRange.label);
  const dateTag = dayjsLib().format('YYYYMMDD_HHmm');
  const base = `${safeName}_${safeLabel}_${dateTag}`;
  const wantKml = format === 'kml' || format === 'both';
  const wantGpx = format === 'gpx' || format === 'both';
  const formats = [];

  if (wantKml) {
    triggerTextDownload(
      `${base}.kml`,
      buildHistoryTrackKml(member.name, points),
      'application/vnd.google-earth.kml+xml'
    );
    formats.push('kml');
  }
  if (wantGpx) {
    // 略延迟第二份下载，避免部分浏览器合并拦截
    setTimeout(() => {
      triggerTextDownload(`${base}.gpx`, buildHistoryTrackGpx(member.name, points), 'application/gpx+xml');
    }, wantKml ? 120 : 0);
    formats.push('gpx');
  }

  return { ok: true, points: points.length, label: timeRange.label, formats };
}

/**
 * 下载弹窗时间预设 → ISO 范围
 * @param {string} preset
 * @param {[any, any]|null} range
 * @param {typeof dayjs} [dayjsLib]
 */
export function resolveDownloadTimeRange(preset, range, dayjsLib = dayjs) {
  const now = dayjsLib();
  if (preset === 'today') {
    return {
      start: now.startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
      label: '当天'
    };
  }
  if (preset === 'last7') {
    return {
      start: now.subtract(6, 'day').startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
      label: '最近7天'
    };
  }
  if (preset === 'last30') {
    return {
      start: now.subtract(29, 'day').startOf('day').toISOString(),
      end: now.endOf('day').toISOString(),
      label: '最近30天'
    };
  }
  if (preset === 'all') {
    return { start: null, end: null, label: '全部历史' };
  }
  const startVal = range && range[0] ? dayjsLib(range[0]) : null;
  const endVal = range && range[1] ? dayjsLib(range[1]) : null;
  if (!startVal || !endVal || !startVal.isValid() || !endVal.isValid()) {
    return null;
  }
  return {
    start: startVal.toISOString(),
    end: endVal.toISOString(),
    label: `${startVal.format('YYYY-MM-DD HH:mm')} ~ ${endVal.format('YYYY-MM-DD HH:mm')}`
  };
}

export function classifyFetchResponseQuality(responseTimeMs) {
  return classifyNetworkQuality(responseTimeMs);
}

/** 取回放用的第一条非空轨迹 */
/** 按名字列表取非空轨迹（保持传入顺序） */
export function pickPlaybackTracksByNames(trackDataMap = {}, names = []) {
  const out = [];
  const list = Array.isArray(names) ? names : [];
  for (const name of list) {
    const track = trackDataMap?.[name] || [];
    if (track.length > 0) out.push({ key: name, track });
  }
  return out;
}

export function pickFirstPlaybackTrack(trackDataMap = {}) {
  const keys = Object.keys(trackDataMap || {});
  for (const key of keys) {
    const track = trackDataMap[key] || [];
    if (track.length > 0) {
      return { key, track };
    }
  }
  return null;
}

export function getHistoryTrackPointCenter(point) {
  if (!point) return null;
  const lng = Number(point.last_longitude ?? point.longitude ?? point.lng);
  const lat = Number(point.last_latitude ?? point.latitude ?? point.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
}

/**
 * 启动逐步回放（不依赖 React isPlaying 闭包）
 */
export function startTrackPlayback(options = {}) {
  const {
    track = [],
    map,
    timerRef,
    stepMs = PLAYBACK_STEP_MS,
    startIndex = 0,
    speed = 1,
    /** 多队员回放时传入各轨起点用于一次 fitBounds；缺省则居中主轨当前点 */
    centerTracks = null,
    onIndex,
    onFinished,
    shouldContinue = () => true,
    /** 回放游标（与在线队员 Marker 分离） */
    playbackMarkersRef = null,
    trackData = null,
    playbackNames = null,
    primaryName = null,
    trackColors = {},
    members = [],
    /** () => number：播放中读最新倍速，滑条实时生效 */
    getSpeed = null,
    /** false：仅续跑定时器（改速重排），不居中 */
    recenter = true
  } = options;

  const readSpeed = () => {
    if (typeof getSpeed === 'function') {
      const v = Number(getSpeed());
      return Math.max(0.25, Number.isFinite(v) && v > 0 ? v : 1);
    }
    const v = Number(speed);
    return Math.max(0.25, Number.isFinite(v) && v > 0 ? v : 1);
  };

  if (!track.length) {
    onFinished?.(false);
    return { stop: () => {} };
  }

  const stop = () => {
    if (timerRef) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      try { delete timerRef.__zhxtPlaybackReschedule; } catch (_) { /* ignore */ }
      try { delete timerRef.__zhxtPlaybackIdx; } catch (_) { /* ignore */ }
    }
  };

  const scheduleNext = (idx) => {
    if (!timerRef) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const effectiveStep = Math.max(16, Math.round(stepMs / readSpeed()));
    timerRef.current = setTimeout(() => playStep(idx + 1), effectiveStep);
  };

  /** 播放中改速：清掉旧 delay，按新倍速从当前帧再排下一步（不居中、不重画开局） */
  if (timerRef) {
    timerRef.__zhxtPlaybackReschedule = () => {
      const idx = Number(timerRef.__zhxtPlaybackIdx);
      if (!Number.isFinite(idx) || idx >= track.length - 1) return;
      scheduleNext(idx);
    };
  }

  const ensureMercatorForMarkers = () => {
    if (!map) return;
    try {
      if (typeof map.setProjection === 'function') {
        const proj = map.getProjection?.();
        if (proj && String(proj.name || proj).toLowerCase() === 'globe') {
          map.setProjection('mercator');
        }
      }
      notifyMemberMarkersProjectionReady(map);
    } catch (_) { /* ignore */ }
  };

  const paintCursors = (idx) => {
    if (!map || !playbackMarkersRef) return;
    const data = trackData && typeof trackData === 'object' ? trackData : { __primary: track };
    const names = Array.isArray(playbackNames) && playbackNames.length
      ? playbackNames
      : Object.keys(data).filter((k) => (data[k] || []).length > 0);
    syncPlaybackCursorMarkers(map, {
      trackData: data,
      names,
      primaryName: primaryName || names[0] || null,
      index: idx,
      trackColors,
      members,
      markersRef: playbackMarkersRef
    });
  };

  /**
   * 开播时居中一次：idx===0 居中起点；暂停后续播则居中当前进度点（再继续播）。
   * 步进过程中不再 flyTo。
   */
  const centerOnce = (idx) => {
    if (!map) return;
    const starts = [];
    const list = Array.isArray(centerTracks) && centerTracks.length
      ? centerTracks
      : [track];
    list.forEach((tr) => {
      const pts = Array.isArray(tr) ? tr : (tr?.track || []);
      if (!pts.length) return;
      const point = pickPlaybackPointForTrack(pts, track, idx) || pts[Math.min(idx, pts.length - 1)] || pts[0];
      const c = getHistoryTrackPointCenter(point);
      if (!c) return;
      const d = toDisplay(map, c[0], c[1]) || c;
      starts.push(d);
    });
    if (!starts.length) {
      const center = getHistoryTrackPointCenter(track[idx]);
      if (!center) return;
      starts.push(toDisplay(map, center[0], center[1]) || center);
    }
    if (starts.length >= 2 && typeof map.fitBounds === 'function') {
      let minLng = starts[0][0];
      let maxLng = starts[0][0];
      let minLat = starts[0][1];
      let maxLat = starts[0][1];
      starts.forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      });
      // 同点时退化为单点居中
      if (minLng === maxLng && minLat === maxLat) {
        const zoom = map.getZoom?.() ?? 14;
        map.easeTo?.({ center: starts[0], zoom, duration: 600, essential: true })
          || map.jumpTo?.({ center: starts[0], zoom });
      } else {
        map.fitBounds(
          [[minLng, minLat], [maxLng, maxLat]],
          { padding: 80, maxZoom: 16, duration: 600, essential: true }
        );
      }
      return;
    }
    const displayCenter = starts[0];
    const zoom = map.getZoom?.() ?? 14;
    if (typeof map.easeTo === 'function') {
      map.easeTo({ center: displayCenter, zoom, duration: 600, essential: true });
    } else if (typeof map.jumpTo === 'function') {
      map.jumpTo({ center: displayCenter, zoom });
    } else if (typeof map.flyTo === 'function') {
      map.flyTo({ center: displayCenter, zoom, speed: 1.2, essential: true });
    }
  };

  const playStep = (idx) => {
    if (!shouldContinue()) return;
    if (timerRef) timerRef.__zhxtPlaybackIdx = idx;
    onIndex?.(idx);
    paintCursors(idx);
    // 步进不再 flyTo / 跟随；每步按最新倍速排程
    if (idx < track.length - 1) {
      scheduleNext(idx);
    } else {
      onFinished?.(true);
    }
  };

  const from = Math.min(Math.max(0, Number(startIndex) || 0), track.length - 1);
  ensureMercatorForMarkers();
  if (recenter !== false) centerOnce(from);
  playStep(from);
  return { stop };
}

export function stopTrackPlayback(timerRef) {
  if (!timerRef) return;
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
  try { delete timerRef.__zhxtPlaybackReschedule; } catch (_) { /* ignore */ }
  try { delete timerRef.__zhxtPlaybackIdx; } catch (_) { /* ignore */ }
}

/** 近似视野离地高度（米） */
export function estimateViewHeight(zoom, lat, screenHeightPx = 800) {
  const R = 6378137;
  const initialResolution = (2 * Math.PI * R) / 256;
  const resolution = initialResolution / Math.pow(2, zoom);
  return Math.round((resolution * screenHeightPx) / 2);
}

/**
 * 拉取单队员历史轨迹
 * @returns {Promise<{ action: string, name?: string, track?: any[], error?: any, message?: string }>}
 */
export async function fetchMemberHistoryTrack(options = {}) {
  const {
    name,
    trackData = {},
    forceRefresh = false,
    findMember,
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    startTime = '',
    endTime = '',
    playbackRange,
    explicitRange = null,
    apiFetch
  } = options;

  if (trackData[name] && !forceRefresh) {
    return { action: 'skip', name };
  }

  const member = findMember?.(name);
  if (!member) {
    console.error('找不到成员:', name);
    return { action: 'missing-member', name };
  }

  const url = buildHistoryTrackUrl(member, {
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    startTime,
    endTime,
    playbackRange,
    explicitRange
  });
  console.log('请求轨迹URL:', url);

  try {
    const res = await apiFetch(url);
    const data = await res.json();
    if (data.success) {
      console.log(`轨迹数据获取成功: ${name}`, data.track.length, '个点');
      return {
        action: 'success',
        name,
        track: data.track || [],
        message: data.message,
        allow_track_fetch: data.allow_track_fetch,
        source: data.source
      };
    }
    console.error('轨迹数据获取失败:', data);
    return {
      action: 'fail',
      name,
      message: data.error || `获取 ${name} 轨迹失败`,
      data
    };
  } catch (error) {
    console.error('获取轨迹失败', error);
    return { action: 'error', name, error, message: `获取 ${name} 轨迹失败` };
  }
}

/** 批量拉取队员历史轨迹 */
export async function fetchAllMemberHistoryTracks(options = {}) {
  const { members = [], onTrack, ...rest } = options;
  if (!members.length) return [];

  const results = [];
  for (const member of members) {
    const result = await fetchMemberHistoryTrack({
      ...rest,
      name: member.name,
      findMember: () => member
    });
    if (result.action === 'success') {
      onTrack?.(member.name, result.track);
    }
    results.push(result);
  }
  return results;
}
